import {
  Clock3,
  Flag,
  Gamepad2,
  Hand,
  Lightbulb,
  PawPrint,
  Plane,
  Search,
  Shapes,
  Smile,
  Soup,
} from 'lucide-react';
import { memo, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useComponentDefaults } from '../../Provider';
import Button from '../Button/Button';
import Input from '../Input/Input';
import Popover from '../Popover/Popover';
import { EMOJI_CATALOG, EMOJI_CATALOG_VERSION } from './emojiCatalog.generated.js';
import EmojiGlyph, { DEFAULT_EMOJI_RENDERING, hasEmojiArtwork } from './EmojiGlyph.jsx';
import { resolveEmojiRendering } from './emojiArtwork.js';
import styles from './EmojiPicker.module.css';

const CATEGORY_CONFIG = Object.freeze([
  { id: 'frequent', icon: Clock3, messageId: 'emojiPicker.category.frequent', label: 'Frequently used' },
  { id: 'Smileys & Emotion', icon: Smile, messageId: 'emojiPicker.category.smileys', label: 'Smileys & emotion' },
  { id: 'People & Body', icon: Hand, messageId: 'emojiPicker.category.people', label: 'People & body' },
  { id: 'Animals & Nature', icon: PawPrint, messageId: 'emojiPicker.category.animals', label: 'Animals & nature' },
  { id: 'Food & Drink', icon: Soup, messageId: 'emojiPicker.category.food', label: 'Food & drink' },
  { id: 'Travel & Places', icon: Plane, messageId: 'emojiPicker.category.travel', label: 'Travel & places' },
  { id: 'Activities', icon: Gamepad2, messageId: 'emojiPicker.category.activities', label: 'Activities' },
  { id: 'Objects', icon: Lightbulb, messageId: 'emojiPicker.category.objects', label: 'Objects' },
  { id: 'Symbols', icon: Shapes, messageId: 'emojiPicker.category.symbols', label: 'Symbols' },
  { id: 'Flags', icon: Flag, messageId: 'emojiPicker.category.flags', label: 'Flags' },
]);

const normalizeQuery = (value) => String(value || '').trim().toLocaleLowerCase();

const SKIN_TONE_PATTERN = /[\u{1F3FB}-\u{1F3FF}]/u;
const SKIN_TONE_REPLACE_PATTERN = /[\u{1F3FB}-\u{1F3FF}]/gu;
const EMOJI_PRESENTATION_SELECTOR_PATTERN = /[\uFE0E\uFE0F]/gu;

export const EMOJI_SKIN_TONES = Object.freeze([
  {
    id: 'default',
    modifier: '',
    swatch: '👋',
    messageId: 'emojiPicker.skinTone.default',
    label: 'Default skin tone',
  },
  {
    id: 'light',
    modifier: '🏻',
    swatch: '👋🏻',
    messageId: 'emojiPicker.skinTone.light',
    label: 'Light skin tone',
  },
  {
    id: 'medium-light',
    modifier: '🏼',
    swatch: '👋🏼',
    messageId: 'emojiPicker.skinTone.mediumLight',
    label: 'Medium-light skin tone',
  },
  {
    id: 'medium',
    modifier: '🏽',
    swatch: '👋🏽',
    messageId: 'emojiPicker.skinTone.medium',
    label: 'Medium skin tone',
  },
  {
    id: 'medium-dark',
    modifier: '🏾',
    swatch: '👋🏾',
    messageId: 'emojiPicker.skinTone.mediumDark',
    label: 'Medium-dark skin tone',
  },
  {
    id: 'dark',
    modifier: '🏿',
    swatch: '👋🏿',
    messageId: 'emojiPicker.skinTone.dark',
    label: 'Dark skin tone',
  },
]);

export const stripEmojiSkinTones = (emoji) =>
  String(emoji || '').replace(SKIN_TONE_REPLACE_PATTERN, '');

const getEmojiVariantKey = (emoji) =>
  stripEmojiSkinTones(emoji).replace(EMOJI_PRESENTATION_SELECTOR_PATTERN, '');

const getRenderableItems = (items, rendering) =>
  rendering === 'native'
    ? items
    : items.filter(
        (item) =>
          hasEmojiArtwork(item.emoji) || resolveEmojiRendering(item.emoji, rendering) === 'native',
      );

const buildSkinToneCatalog = (items, rendering = DEFAULT_EMOJI_RENDERING) => {
  const supportedItems = getRenderableItems(items, rendering);
  const baseItems = supportedItems.filter((item) => !SKIN_TONE_PATTERN.test(item.emoji));
  const baseByEmoji = new Map(baseItems.map((item) => [getEmojiVariantKey(item.emoji), item]));
  const variantByBaseAndModifier = new Map();

  supportedItems.forEach((item) => {
    const modifiers = item.emoji.match(SKIN_TONE_REPLACE_PATTERN) || [];
    if (!modifiers.length || !modifiers.every((modifier) => modifier === modifiers[0])) return;
    const baseKey = getEmojiVariantKey(item.emoji);
    if (!baseByEmoji.has(baseKey)) return;
    variantByBaseAndModifier.set(`${baseKey}:${modifiers[0]}`, item);
  });

  return { baseItems, baseByEmoji, variantByBaseAndModifier };
};

const projectSkinToneFromCatalog = (item, skinTone, variantByBaseAndModifier) => {
  const tone = EMOJI_SKIN_TONES.find((option) => option.id === skinTone) || EMOJI_SKIN_TONES[0];
  if (!tone.modifier) return item;
  return variantByBaseAndModifier.get(`${getEmojiVariantKey(item.emoji)}:${tone.modifier}`) || item;
};

const defaultSkinToneCatalog = buildSkinToneCatalog(EMOJI_CATALOG);

export const projectEmojiSkinTone = (item, skinTone = 'default') =>
  projectSkinToneFromCatalog(item, skinTone, defaultSkinToneCatalog.variantByBaseAndModifier);

const matchesQuery = (item, query) => {
  if (!query) return true;
  return [item.emoji, item.name, item.group, item.subgroup, item.codePoints, ...item.keywords]
    .join(' ')
    .toLocaleLowerCase()
    .includes(query);
};

export const getFrequentEmojiItems = (
  usage = {},
  limit = 48,
  items = EMOJI_CATALOG,
  rendering = DEFAULT_EMOJI_RENDERING,
) => {
  const supportedItems = getRenderableItems(items, rendering);
  const itemByValue = new Map(supportedItems.map((item) => [item.emoji, item]));
  const baseByValue = new Map(
    supportedItems
      .filter((item) => !SKIN_TONE_PATTERN.test(item.emoji))
      .map((item) => [getEmojiVariantKey(item.emoji), item]),
  );
  const aggregated = new Map();

  Object.entries(usage || {}).forEach(([emoji, record]) => {
    const exactItem = itemByValue.get(emoji) || null;
    const baseItem = baseByValue.get(getEmojiVariantKey(emoji)) || exactItem;
    if (!baseItem || SKIN_TONE_PATTERN.test(baseItem.emoji)) return;
    const previous = aggregated.get(baseItem.emoji) || {
      count: 0,
      lastUsedAt: 0,
      item: baseItem,
    };
    aggregated.set(baseItem.emoji, {
      ...previous,
      count: previous.count + (Number(record?.count) || 0),
      lastUsedAt: Math.max(previous.lastUsedAt, Number(record?.lastUsedAt) || 0),
    });
  });

  return Array.from(aggregated.values())
    .sort((left, right) => right.count - left.count || right.lastUsedAt - left.lastUsedAt)
    .slice(0, limit)
    .map((record) => record.item);
};

export const EmojiPicker = memo(function EmojiPicker(props) {
  const intl = useIntl();
  const merged = useComponentDefaults('EmojiPicker', props, {
    category: 'frequent',
    query: '',
    usage: {},
    mode: 'emoji',
    items: EMOJI_CATALOG,
    categories: CATEGORY_CONFIG,
    skinTone: 'default',
    rendering: DEFAULT_EMOJI_RENDERING,
  });
  const {
    category = 'frequent',
    categories = CATEGORY_CONFIG,
    className = '',
    mode = 'emoji',
    items = EMOJI_CATALOG,
    onCategoryChange,
    onQueryChange,
    onSelect,
    query = '',
    skinTone = 'default',
    onSkinToneChange,
    rendering = DEFAULT_EMOJI_RENDERING,
    usage = {},
  } = merged;
  const normalizedQuery = normalizeQuery(query);
  const selectedSkinTone =
    EMOJI_SKIN_TONES.find((option) => option.id === skinTone) || EMOJI_SKIN_TONES[0];
  const skinToneCatalog = useMemo(
    () => buildSkinToneCatalog(items, rendering),
    [items, rendering],
  );
  const frequentItems = useMemo(
    () => getFrequentEmojiItems(usage, 48, items, rendering),
    [items, rendering, usage],
  );
  const visibleItems = useMemo(() => {
    if (normalizedQuery) {
      return skinToneCatalog.baseItems.filter((item) => matchesQuery(item, normalizedQuery));
    }
    if (category === 'frequent') {
      return frequentItems;
    }
    return skinToneCatalog.baseItems.filter((item) => item.group === category);
  }, [category, frequentItems, normalizedQuery, skinToneCatalog.baseItems]);
  const projectedItems = useMemo(
    () =>
      visibleItems.map((item) => ({
        baseItem: item,
        displayItem: projectSkinToneFromCatalog(
          item,
          selectedSkinTone.id,
          skinToneCatalog.variantByBaseAndModifier,
        ),
      })),
    [selectedSkinTone.id, skinToneCatalog.variantByBaseAndModifier, visibleItems],
  );

  const selectedCategory = categories.some((item) => item.id === category)
    ? category
    : 'frequent';
  const headingConfig = categories.find((item) => item.id === selectedCategory);
  const pickerLabel = intl.formatMessage({
    id: mode === 'reaction' ? 'emojiPicker.reactionPicker' : 'emojiPicker.emojiPicker',
    defaultMessage: mode === 'reaction' ? 'Reaction picker' : 'Emoji picker',
  });
  const searchLabel = intl.formatMessage({
    id: mode === 'reaction' ? 'emojiPicker.searchReactions' : 'emojiPicker.searchEmoji',
    defaultMessage: mode === 'reaction' ? 'Search reactions' : 'Search emoji',
  });
  const searchPlaceholder = intl.formatMessage({
    id: mode === 'reaction' ? 'emojiPicker.findReaction' : 'emojiPicker.findEmoji',
    defaultMessage:
      mode === 'reaction' ? 'Find the perfect reaction' : 'Find the perfect emoji',
  });

  return (
    <section
      className={`${styles.picker} ${className}`.trim()}
      aria-label={pickerLabel}
      data-emoji-catalog-version={EMOJI_CATALOG_VERSION}
      data-emoji-picker-root="true"
    >
      <div className={styles.searchRow}>
        <div className={styles.searchField}>
          <Search className={styles.searchIcon} size={20} aria-hidden />
          <Input
            className={styles.searchInput}
            value={query}
            onChange={(event) => onQueryChange?.(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchLabel}
            autoComplete="off"
          />
        </div>
        <Popover
          trigger="click"
          placement="bottomRight"
          closeOnBlur={false}
          closeOnContentClick
          arrow={false}
          zIndex="var(--z-dropdown)"
          getPopupContainer={(triggerNode) =>
            triggerNode?.closest?.('[data-emoji-picker-root="true"]')
          }
          contentClassName={styles.skinTonePopoverContent}
          surfaceStyle={{ padding: 0, minWidth: 0, width: 'max-content' }}
          content={
            <div
              className={styles.skinToneMenu}
              role="group"
              aria-label={intl.formatMessage({
                id: 'emojiPicker.skinTone',
                defaultMessage: 'Skin tone',
              })}
            >
              {EMOJI_SKIN_TONES.map((option) => (
                <FormattedMessage
                  id={option.messageId}
                  defaultMessage={option.label}
                  key={option.id}
                >
                  {(localizedLabel) => (
                    <Button
                      type="text"
                      htmlType="button"
                      variant="text"
                      shape="circle"
                      className={`${styles.skinToneOption} ${selectedSkinTone.id === option.id ? styles.skinToneOptionActive : ''}`.trim()}
                      aria-label={localizedLabel}
                      aria-pressed={selectedSkinTone.id === option.id}
                      title={localizedLabel}
                      onClick={() => onSkinToneChange?.(option.id)}
                    >
                      <EmojiGlyph
                        emoji={option.swatch}
                        size={28}
                        className={styles.skinToneSwatch}
                        rendering={rendering}
                      />
                    </Button>
                  )}
                </FormattedMessage>
              ))}
            </div>
          }
        >
          <Button
            type="text"
            htmlType="button"
            variant="text"
            shape="circle"
            className={styles.skinToneTrigger}
            aria-label={intl.formatMessage({
              id: 'emojiPicker.skinTone',
              defaultMessage: 'Skin tone',
            })}
            title={intl.formatMessage({
              id: selectedSkinTone.messageId,
              defaultMessage: selectedSkinTone.label,
            })}
          >
            <EmojiGlyph
              emoji={selectedSkinTone.swatch}
              size={28}
              className={styles.skinToneSwatch}
              rendering={rendering}
            />
          </Button>
        </Popover>
      </div>
      <div className={styles.body}>
        <nav
          className={styles.categoryRail}
          aria-label={intl.formatMessage({
            id: 'emojiPicker.categories',
            defaultMessage: 'Emoji categories',
          })}
        >
          {categories.map(({ id, icon: CategoryIcon, messageId, label }) => (
            <FormattedMessage id={messageId} defaultMessage={label} key={id}>
              {(localizedLabel) => (
                <Button
                  type="text"
                  htmlType="button"
                  variant="text"
                  shape="circle"
                  size="sm"
                  className={`${styles.categoryButton} ${selectedCategory === id && !normalizedQuery ? styles.categoryButtonActive : ''}`.trim()}
                  aria-label={localizedLabel}
                  aria-pressed={selectedCategory === id && !normalizedQuery}
                  title={localizedLabel}
                  onClick={() => {
                    onQueryChange?.('');
                    onCategoryChange?.(id);
                  }}
                >
                  <CategoryIcon size={20} aria-hidden />
                </Button>
              )}
            </FormattedMessage>
          ))}
        </nav>
        <div className={styles.results} role="region" aria-live="polite">
          <h2 className={styles.heading}>
            {normalizedQuery ? (
              <FormattedMessage id="emojiPicker.searchResults" defaultMessage="Search results" />
            ) : (
              <FormattedMessage
                id={headingConfig?.messageId}
                defaultMessage={headingConfig?.label}
              />
            )}
          </h2>
          {projectedItems.length ? (
            <div className={styles.grid} role="list">
              {projectedItems.map(({ baseItem, displayItem }) => (
                <div key={baseItem.codePoints} className={styles.emojiItem} role="listitem">
                  <Button
                    type="text"
                    htmlType="button"
                    variant="text"
                    shape="default"
                    className={styles.emojiButton}
                    aria-label={displayItem.name}
                    title={displayItem.name}
                    onClick={() =>
                      onSelect?.({
                        ...displayItem,
                        baseEmoji: baseItem.emoji,
                        skinTone: selectedSkinTone.id,
                      })
                    }
                  >
                    <EmojiGlyph
                      emoji={displayItem.emoji}
                      size={28}
                      className={styles.emoji}
                      rendering={rendering}
                    />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.empty}>
              <FormattedMessage
                id={
                  normalizedQuery ? 'emojiPicker.noResults' : 'emojiPicker.noFrequentlyUsed'
                }
                defaultMessage={
                  normalizedQuery
                    ? 'No emoji match your search.'
                    : 'Your frequently used emoji will appear here.'
                }
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
});

export { EMOJI_CATALOG_VERSION, CATEGORY_CONFIG as EMOJI_PICKER_CATEGORIES };
